// src/utils/resolveEducatorUid.js
//
// "Which educator owns this code?" — asked in one place, one way.
//
// ===========================================================================
// WHY THIS FILE EXISTS
// ===========================================================================
// This lookup was written three separate times, in three files, with three
// DIFFERENT hardcoded role lists:
//
//   components/tools/VideoTool.js   ["educator", "nc-1st-educator", "administrator"]
//   utils/progressMirror.js         ["educator", "nc-1st-educator", "administrator"]
//   games/UniversalQuizGame.js      ["educator", `${state}_${grade}_educator`,
//                                    `${state}-${grade}-educator`, "administrator", "admin"]
//
// Real roles in this app look like `va-3rd-educator` and `nc-4th-educator`.
// So for a Virginia 3rd-grade teacher the first two lists match NOTHING. Both
// call sites then hit:
//
//     if (eduSnap.empty) return;
//
// and return silently. No error, no console line, no data. That is why video
// and music plays never appeared in the classroom while game scores sometimes
// did — the quiz file happened to construct the hyphenated variant.
//
// There was a second failure mode stacked on top of the first. That query is
// a `list` against /users, and firestore.rules only grants `list` on /users to
// admins (see the comment in hooks/useClassEducator.js). So even a student
// whose educator's role DID happen to match would get a permission error,
// caught by the same silent `return`.
//
// ===========================================================================
// WHAT REPLACES IT
// ===========================================================================
// `educatorDirectory/{code}` already exists, is already written on code
// allocation (utils/educatorCode.js and functions/educatorCode.js both write
// it), and already contains `uid`. One document GET:
//
//   - no role list to drift out of date
//   - no `list` permission needed, so it works for students
//   - one read instead of a collection scan
//
// Session-cached, because a student moving between Math, Science and Social
// Studies would otherwise re-read the same document on every page and the
// answer cannot change mid-visit.

import { doc, getDoc } from "firebase/firestore";

const cache = new Map();

/**
 * Turns an educator code into that educator's uid.
 *
 * @param {import("firebase/firestore").Firestore} db
 * @param {string|number} educatorCode
 * @returns {Promise<string|null>} uid, or null when the code belongs to nobody
 */
export async function resolveEducatorUid(db, educatorCode) {
  const code = String(educatorCode || "").trim();
  if (!db || !code) return null;

  if (cache.has(code)) return cache.get(code);

  try {
    const snap = await getDoc(doc(db, "educatorDirectory", code));
    const uid = snap.exists() ? snap.data()?.uid || null : null;

    // A miss is cached too. A code that matches nobody is almost always a
    // typo, and re-reading it on every video play costs the same as reading
    // a real one.
    cache.set(code, uid);
    return uid;
  } catch (err) {
    // Deliberately NOT cached — a transient failure shouldn't poison the
    // rest of the session. Logged rather than swallowed: the silent version
    // of this is what hid the bug for months.
    console.warn(
      "[resolveEducatorUid] directory lookup failed for",
      code,
      "—",
      err?.code || err?.message
    );
    return null;
  }
}

/**
 * Clears the cache. Call after an educator's code changes, which currently
 * only happens in admin tooling.
 */
export function clearEducatorUidCache() {
  cache.clear();
}

export default resolveEducatorUid;
